import React from 'react';
import { Link } from 'react-router-dom';
import { renderParagraphs } from '../utils/textFormatters';
import useDocumentTitle from '../hooks/useDocumentTitle';
import '../styles/animations.css'; // Import animations CSS

// Define collaborators array
const collaborators = [
  { channelUrl: "https://hoplite.gg/", name: "Hoplite Network", image: "hoplitelogo.jfif" },
  { channelUrl: "https://www.youtube.com/@wemmbumc", name: "Wemmbu", image: "Wemmbuicon.webp" },
  { channelUrl: "https://discord.gg/p5SZTTepM2", name: "Phytor Network", image: "phytormnc.jpg" },
  { channelUrl: "https://discord.gg/Q3XQJkVAMR", name: "Altar SMP", image: "altaricon.png" },
  { channelUrl: "https://www.youtube.com/@SpeedSilver", name: "SpeedSilver", image: "speedsilver.jpg" },
  { channelUrl: "https://www.youtube.com/@Wallibear", name: "Wallibear", image: "wallibear.jpg" },
  { channelUrl: "https://www.youtube.com/@MylesMC", name: "MylesMC", image: "myles.jpg" },
  { channelUrl: "https://www.youtube.com/@MindOfNeo", name: "mindofneo", image: "mindofneo.jpg" },
  { channelUrl: "https://www.youtube.com/@epaxialyt", name: "Epaxial", image: "epax profile.jpg" },
  { channelUrl: "https://www.youtube.com/@OGQndres", name: "OGQndres", image: "andres.png" },
  { channelUrl: null, name: "EVNT / Moonrock", image: "evntgames_logo.jpg" },
  { channelUrl: "https://www.youtube.com/@strengthsmp", name: "Strength SMP", image: "strengthsmp.jpg" },
  { channelUrl: null, name: "PVP Club (2025)", image: "pvpclub.webp" },
];

// YouTube Collaborator Component
const YoutubeCollaborator = ({ channelUrl, name, image }) => {
  const displayName = name || (channelUrl && channelUrl.split('@')[1]) || 'Collaborator';
  const imageSrc = image
    ? `${process.env.PUBLIC_URL}/images/collaborators/${image}`
    : `${process.env.PUBLIC_URL}/images/collaborators/${displayName.toLowerCase()}.jpg`;

  const inner = (
    <>
      <div className="flex items-center justify-center h-[80px] mb-2">
        <div className="w-16 h-16 rounded-full bg-gray-300 overflow-hidden shadow-md">
          <img
            src={imageSrc}
            alt={displayName}
            className="w-full h-full object-cover"
            onError={(e) => {
              e.target.src = `${process.env.PUBLIC_URL}/images/default-avatar.png`;
            }}
          />
        </div>
      </div>
      <p className="text-sm font-medium text-center text-[#294C6F] dark:text-[#4f9cf9]">{displayName}</p>
    </>
  );

  if (!channelUrl) {
    return (
      <div className="flex flex-col min-w-[120px] mx-6">
        {inner}
      </div>
    );
  }

  return (
    <a
      href={channelUrl}
      target="_blank"
      rel="noopener noreferrer"
      className="flex flex-col min-w-[120px] mx-6 transition-transform hover:scale-110"
    >
      {inner}
    </a>
  );
};

const HomePage = ({ setActiveSection, setActiveProject }) => {
  useDocumentTitle('Home');
  
  return (
    <>
      {/* Full-width video banner - adjusted height to compensate for navbar */}
      <div className="relative h-[calc(95vh)] w-full overflow-hidden bg-[#333333] -mt-24">
        <video
          autoPlay
          loop
          muted
          playsInline
          className="absolute inset-0 w-full h-full object-cover filter blur-[3px]"
        >
          <source src={`${process.env.PUBLIC_URL}/videos/background.mp4`} type="video/mp4" />
        </video>
        <div className="absolute inset-0 bg-black bg-opacity-50"></div>
        
        {/* Text overlay - adjusted to account for navbar */}
        <div className="absolute inset-0 flex flex-col items-center justify-center text-white text-center px-4 mt-8">
          <h1 className="text-7xl font-bold mb-16">Invictable</h1>
          
          <div className="text-3xl font-light mb-16">
            <p className="mb-2">Game Designer</p>
            <p className="mb-2">Project Manager</p>
            <p>Developer</p>
          </div>
          
          {/* Refined Portfolio Button - 20% larger with more rounded corners */}
          <Link 
            to="/portfolio" 
            className="px-14 py-5 bg-[#2978C8]/90 hover:bg-[#2978C8] text-white text-2xl tracking-wider font-medium rounded-lg transition-all duration-300 transform hover:translate-y-[-2px] shadow-md hover:shadow-lg backdrop-blur-sm"
          >
            View Portfolio
          </Link>
        </div>
      </div>

      {/* Content sections - updated to match portfolio/resume structure */}
      <div className="pt-32 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="mb-20">
            <h2 className="text-3xl font-bold mb-6 text-[#2978C8] dark:text-[#4f9cf9]">About Me</h2>
            {renderParagraphs([
              "Hi. I'm Invic.",
              "I'm a passionate Game Designer, Project Manager, and Developer with extensive experience in the Minecraft space.",
              "I've played Minecraft since 2012, have been actively part of the community since 2016, and have been designing & developing experiences in Minecraft since 2021.",
              "I've been interested in the art of game design my entire life. From 2014-2016, I developed Call of Duty: Zombie maps. After becoming more involved in the Minecraft community, I built custom maps for gamemodes like Skywars and Bedwars to improve my understanding of level design elements.",
              "I've also graduated from college with a Bachelor's Degree in Computer Science with a Game Programming concentration.",
              " ",
              "My most notable work is on the *Hoplite Network* where I designed and managed the creation of its flagship gamemode, Battle Royale. After Hoplite released, I became a full project manager. Here are a short list of my accomplishments during that time:",
              "- I designed the specifics of how every aspect of the Battle Royale gamemode functions and managed developers to implement these elements.",
              "- I led a cross-functional team of *10+ developers, builders, and artists*.",
              "- I created and managed volunteer teams for translations, QA, and community feedback.",
              "- I managed the development and execution of a successful marketing event for promoting a new Battle Royale gamemode.",
              
              "By the end of my time at Hoplite, the network had seen over *350,000 unique players* and regularly averaged *1000 concurrent players*.",
              " ",
              "I love the creative process involved in every step of the game development process and strive to create engaging, high-quality experiences for players. It's deeply rewarding to see my work being enjoyed by others, and I'm always looking for new opportunities to make an impact.",
              " ",
              "If you'd like to see my work on Hoplite or other projects more in depth, feel free to check out my portfolio.",
            ])}
          </div>

          <div className="mb-20">
            <h2 className="text-3xl font-bold mb-10 text-[#2978C8] dark:text-[#4f9cf9]">People & groups I've Worked With</h2>
            <div className="relative overflow-hidden">
              <div className="flex items-end animate-scroll gap-8 py-4">
                {[...collaborators, ...collaborators].map((collab, index) => (
                  <YoutubeCollaborator 
                    key={index} 
                    channelUrl={collab.channelUrl}
                    name={collab.name}
                    image={collab.image}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default HomePage; 